import { useEffect, useState } from "react";
import { Button, Checkbox, Drawer, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Popover, Select, TextField, useMediaQuery } from "@mui/material";
import { ChevronDown, Sliders, X } from "react-feather";
import { formatPrice } from "../shopApi";
import styles from "../shop.module.css";

export const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "best_selling", label: "Best selling" },
  { value: "title_asc", label: "Alphabetically, A-Z" },
  { value: "title_desc", label: "Alphabetically, Z-A" },
  { value: "price_asc", label: "Price, low to high" },
  { value: "price_desc", label: "Price, high to low" },
  { value: "date_asc", label: "Date, old to new" },
  { value: "date_desc", label: "Date, new to old" },
];

// Availability is one value ("in", "out" or none); ticking both boxes is the same as none
const availabilityFrom = (checked) => (checked.in && !checked.out ? "in" : checked.out && !checked.in ? "out" : "");

const AvailabilityFields = ({ filters, facets, onChange }) => {
  const checked = { in: filters.availability === "in", out: filters.availability === "out" };
  const toggle = (key) => onChange({ availability: availabilityFrom({ ...checked, [key]: !checked[key] }) });
  return (<div className={styles.FilterFields}>
    <FormControlLabel control={<Checkbox size="small" checked={checked.in} onChange={() => toggle("in")} />} label={`In stock (${facets?.in_stock ?? 0})`} />
    <FormControlLabel control={<Checkbox size="small" checked={checked.out} onChange={() => toggle("out")} disabled={!facets?.out_of_stock && !checked.out} />} label={`Out of stock (${facets?.out_of_stock ?? 0})`} />
    {facets?.on_sale > 0 && <FormControlLabel control={<Checkbox size="small" checked={filters.on_sale === "1"} onChange={(e) => onChange({ on_sale: e.target.checked ? "1" : "" })} />} label={`On sale (${facets.on_sale})`} />}
  </div>);
};

const PriceFields = ({ filters, facets, onChange, onDone }) => {
  const [min, setMin] = useState(filters.price_min || "");
  const [max, setMax] = useState(filters.price_max || "");
  useEffect(() => { setMin(filters.price_min || ""); setMax(filters.price_max || ""); }, [filters.price_min, filters.price_max]);

  const apply = () => {
    onChange({ price_min: min, price_max: max });
    onDone && onDone();
  };

  return (<div className={styles.FilterFields}>
    <p className={styles.FilterHint}>The highest price is {formatPrice(facets?.max_price)}</p>
    <div className={styles.PriceRow}>
      <TextField size="small" type="number" label="From" value={min} onChange={(e) => setMin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} inputProps={{ min: 0, step: "0.01" }} InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
      <TextField size="small" type="number" label="To" value={max} onChange={(e) => setMax(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} inputProps={{ min: 0, step: "0.01" }} InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
    </div>
    <Button size="small" className={styles.PrimaryBU} onClick={apply}>Apply</Button>
  </div>);
};

const SortSelect = ({ filters, onChange, fullWidth }) => (<FormControl size="small" className={fullWidth ? undefined : styles.Sort} fullWidth={fullWidth}>
  <InputLabel>Sort by</InputLabel>
  <Select label="Sort by" value={filters.sort || "featured"} onChange={(e) => onChange({ sort: e.target.value === "featured" ? "" : e.target.value })}>
    {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
  </Select>
</FormControl>);

// The chips under the bar: one per active filter, plus Remove all
const activeChips = (filters) => {
  const chips = [];
  if (filters.availability === "in") chips.push({ key: "availability", label: "In stock", clear: { availability: "" } });
  if (filters.availability === "out") chips.push({ key: "availability", label: "Out of stock", clear: { availability: "" } });
  if (filters.on_sale === "1") chips.push({ key: "on_sale", label: "On sale", clear: { on_sale: "" } });
  if (filters.price_min || filters.price_max) {
    const label = `${filters.price_min ? formatPrice(filters.price_min) : "£0.00"} – ${filters.price_max ? formatPrice(filters.price_max) : "any"}`;
    chips.push({ key: "price", label, clear: { price_min: "", price_max: "" } });
  }
  return chips;
};

/**
 * Store-style "Filter and sort": availability (with counts), price range, on sale, sort and the product count.
 * filters: { availability, price_min, price_max, on_sale, sort } (strings); onChange(partial).
 */
const FilterSort = ({ filters, facets, total, onChange, search }) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [anchor, setAnchor] = useState({ el: null, panel: "" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const chips = activeChips(filters);
  const count = `${total} item${total === 1 ? "" : "s"}`;
  const close = () => setAnchor({ el: null, panel: "" });
  const availabilityOn = !!(filters.availability || filters.on_sale);
  const priceOn = !!(filters.price_min || filters.price_max);
  const sortLabel = SORT_OPTIONS.find((o) => o.value === (filters.sort || "featured"))?.label;

  return (<>
    {!isMobile && (<div className={styles.FilterBar}>
      {search && <div className={styles.FilterSearch}>{search}</div>}
      <Button className={`${styles.FilterBU} ${availabilityOn ? styles.FilterBUOn : ""}`} endIcon={<ChevronDown size={16} />} onClick={(e) => setAnchor({ el: e.currentTarget, panel: "availability" })}>Availability</Button>
      <Button className={`${styles.FilterBU} ${priceOn ? styles.FilterBUOn : ""}`} endIcon={<ChevronDown size={16} />} onClick={(e) => setAnchor({ el: e.currentTarget, panel: "price" })}>Price</Button>
      <div className={styles.FilterRight}>
        <span className={styles.ProductCount}>{count}</span>
        <SortSelect filters={filters} onChange={onChange} />
      </div>
    </div>)}

    {isMobile && (<>
      {search && <div className={styles.FilterSearchMobile}>{search}</div>}
      <div className={styles.FilterBar}>
        <Button className={`${styles.FilterBU} ${chips.length ? styles.FilterBUOn : ""}`} startIcon={<Sliders size={16} />} onClick={() => setDrawerOpen(true)}>Filter{chips.length ? ` (${chips.length})` : ""} · {sortLabel}</Button>
        <span className={styles.ProductCount}>{count}</span>
      </div>
    </>)}

    {chips.length > 0 && (<div className={styles.ActiveChips}>
      {chips.map((c) => (<button type="button" key={c.key} className={styles.ActiveChip} onClick={() => onChange(c.clear)} aria-label={`Remove filter ${c.label}`}>{c.label} <X size={14} /></button>))}
      <button type="button" className={styles.RemoveAll} onClick={() => onChange({ availability: "", price_min: "", price_max: "", on_sale: "" })}>Remove all</button>
    </div>)}

    <Popover open={!!anchor.el} anchorEl={anchor.el} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} PaperProps={{ className: styles.FilterPopover }}>
      <div className={styles.FilterPopoverHead}>
        <span>{anchor.panel === "price" ? "Price" : "Availability"}</span>
        <button type="button" className={styles.RemoveAll} onClick={() => onChange(anchor.panel === "price" ? { price_min: "", price_max: "" } : { availability: "", on_sale: "" })}>Reset</button>
      </div>
      {anchor.panel === "availability" && <AvailabilityFields filters={filters} facets={facets} onChange={onChange} />}
      {anchor.panel === "price" && <PriceFields filters={filters} facets={facets} onChange={onChange} onDone={close} />}
    </Popover>

    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ className: styles.FilterDrawer }}>
      <div className={styles.DrawerHead}>
        <div>
          <strong>Filter and sort</strong>
          <span className={styles.ProductCount}>{count}</span>
        </div>
        <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close"><X size={20} /></IconButton>
      </div>
      <div className={styles.DrawerBody}>
        <p className={styles.DrawerSection}>Availability</p>
        <AvailabilityFields filters={filters} facets={facets} onChange={onChange} />
        <p className={styles.DrawerSection}>Price</p>
        <PriceFields filters={filters} facets={facets} onChange={onChange} />
        <p className={styles.DrawerSection}>Sort by</p>
        <SortSelect filters={filters} onChange={onChange} fullWidth />
      </div>
      <div className={styles.DrawerFoot}>
        <Button className={styles.SecondaryBU} onClick={() => onChange({ availability: "", price_min: "", price_max: "", on_sale: "" })}>Remove all</Button>
        <Button className={styles.PrimaryBU} onClick={() => setDrawerOpen(false)}>Show {count}</Button>
      </div>
    </Drawer>
  </>);
};

export default FilterSort;
